<template>
  <v-dialog
    v-model="dialog"
    :disabled="disabled"
    persistent fullscreen hide-overlay>
    <template v-slot:activator="{ on }">
      <v-btn v-if="name"
            v-on="on"
            :class="{ 'px-2' : !largeBtn && name, 'px-3': largeBtn && name, 'px-0': !name }"
            color="primary"
            :disabled="disabled"
            :large="largeBtn">
        <v-icon left>{{ icon }}</v-icon>
        {{ generateName(name) }}
      </v-btn>
      <v-btn v-if="!name"
            v-on="on"
            :class="{ 'px-2' : !largeBtn && name, 'px-3': largeBtn && name, 'px-0': !name }"
            color="primary"
            :disabled="disabled"
            :large="largeBtn"
            icon>
        <v-icon>{{ icon }}</v-icon>
      </v-btn>
    </template>
    <v-card ref="dialog">
      <v-toolbar dark color="primary darken-2">
        <v-btn icon dark @click="dismiss">
          <v-icon aria-hidden="false" role="button">close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ generateName(name) || 'Create Accounts'}} - {{ generateName(steps[currentStep - 1].name) | capitalize }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-layout align-start justify-end>
          <change-company :editable="editableCompany"/>
        </v-layout>
        <v-toolbar-items>
          <v-flex mt-4 ml-2>
            <v-chip color="black" :class="`${server.color}--text`" label disabled>
              <v-avatar>
                <v-icon>domain</v-icon>
              </v-avatar>
              {{ server.name }}
              <br>
              {{ server.platform }}
            </v-chip>
          </v-flex>
        </v-toolbar-items>
      </v-toolbar>
      <v-stepper v-model="currentStep" alt-labels>
        <v-stepper-header class="elevation-0">
          <template v-for="(step, i) in activeSteps">
            <v-stepper-step
              :editable="currentValid && step.editable && highestStep > i && currentStep !== i + 1"
              :complete="currentStep > i + 1"
              :key="`stepper-step-${i}`"
              :step="i + 1">{{ generateName(step.name) | capitalize }}
            </v-stepper-step>
            <v-divider :key="`divider-${i}`" v-if="i < steps.length - 1"/>
          </template>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content v-for="(step, i) in steps" :step="i + 1" :key="`stepper-${i}`">
            <component
              :is="step.component"
              :step="currentStep"
              :data="data"
              :update="update"
              @valid="updateValid"
              ref="steps"
              :key="`component-${instance}-${i}`"/>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { Bus } from '../bus'
import { servers } from '../helpers'
import ChangeCompany from './ChangeCompany'

export default {
  name: 'FormDialog',
  components: { ChangeCompany },
  props: {
    /**
     * The icon to be displayed on the button
     */
    icon: String,
    /**
     * Name to be shown on the button and in the form dialog's title (top bar)
     */
    name: [String, Function],
    /**
     * The sequence of steps that compose the form
     */
    steps: Array,
    /**
     * Initial data (for update), if none is set we are creating something new
     */
    initialData: {
      type: Object,
      default: () => ({ create: true })
    },
    /**
     * If true, the current company displayed in the top bar can be changed
     */
    editableCompany: {
      type: Boolean,
      default: false
    },
    /**
     * If true, the activator button is larger
     */
    largeBtn: {
      type: Boolean,
      default: false
    },
    disabled: Boolean
  },
  data () {
    return {
      dialog: false,
      currentStep: 1,
      highestStep: 1,
      data: {},
      instance: 0,
      timeout: null,
      loading: false,
      currentValid: true,
      server: servers[this.$store.state.auth.server]
    }
  },
  computed: {
    activeSteps () {
      if (!this.steps || !this.steps.length) {
        return []
      }
      return this.steps.filter(step => !step.excludeOnAssessmentStarted || !this.assessmentsStarted)
    }
  },
  mounted () {
    Bus.$on('scroll-dialog', (position = -1) => {
      const dialog = this.$refs.dialog.$el.parentElement
      dialog.scrollTo(0, position === -1 ? dialog.scrollHeight : position)
    })
  },
  watch: {
    /**
     * When the dialog is opened, pass the initial data to first form step and call its .entered() method
     *
     * @param shown
     */
    dialog (shown) {
      this.$nextTick(() => {
        if (shown) {
          this.update(this.initialData)
          this.$refs.steps[0].entered(this.activeSteps)
        }
      })
    },
    /**
     * When changing the steps, trigger the .entered() method of the current step
     *
     * @param step
     */
    currentStep (step) {
      this.$refs.steps[step - 1].entered(this.activeSteps)
    }
  },
  methods: {
    /**
     * Go to the next step. If there are no more steps, submit the form
     */
    next () {
      if (this.currentStep === this.activeSteps.length) {
        this.submit()
      } else {
        this.currentStep++
        this.highestStep = Math.max(this.currentStep, this.highestStep)
      }
    },
    /**
     * Update callback to be passed to form steps
     *
     * @param data
     * @param proceed
     */
    update (data, proceed = false) {
      Object.keys(data).forEach(key => {
        this.$set(this.data, key, data[key])
      })
      if (proceed) {
        this.next()
      }
    },
    /**
     * Dismiss the form, leave time for the dialog to slide off the screen before clearing the data
     */
    dismiss () {
      this.dialog = false
      this.loading = false
      this.timeout = setTimeout(this.reset, 500)
      Bus.$emit('editAccountSteps', { updateType: 'dismiss' })
    },
    /**
     * Submit the form, pass "success" and "error" callbacks to the parent
     */
    submit () {
      this.loading = true
      this.$emit('submit', { data: this.data, success: this.dismiss, error: this.error })
    },
    /**
     * Callback passed to the parent component
     */
    error () {
      this.loading = false
    },
    /**
     * Clear the form data and reset it to the initial state
     */
    reset () {
      this.data = {}
      this.currentStep = 1
      this.highestStep = 1
      this.instance++ // Re-render components to clear their data
    },
    /**
     * Mark the current step as valid
     *
     * @param valid
     */
    updateValid (valid) {
      this.currentValid = valid
    },
    /**
     * Generate step name
     *
     * this to allow dynamically changing step name according to some data
     *
     * @param {String | Function} name
     */
    generateName (name) {
      return typeof name === 'function'
        ? name(this.data)
        : name
    }
  },
  beforeDestroy () {
    clearTimeout(this.timeout)
  }
}
</script>

<style lang="sass">
.slide-left-enter, .slide-left-leave-to
  .v-chip
    border-radius: 0 !important

.v-dialog
  overflow-x: hidden

.rules-optional .error--text
  color: #FB8C00 !important
</style>
